import { openChatCommand } from '../features/ai-chat/lib/chatCommandHandoff.js';
import { commandRequest } from '../agents/context/ui-command-registry.js';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Plus,
  LayoutDashboard,
  Crown,
  Briefcase,
  Code2,
  Palette,
  Settings,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Card, Select } from 'antd';

import { ErrorState } from '../shared/ui/ErrorState.js';

import {
  ActivePipelinesWidget,
  AgentStatusWidget,
  AttentionQueueWidget,
  BurndownWidget,
  CalendarWidget,
  FocusTodayWidget,
  MyDayWidget,
  NotificationCenterWidget,
  OnboardingChecklistWidget,
  OpenTasksWidget,
  PipelineBoardWidget,
  QuickActionsWidget,
  RecentActivityWidget,
  RecentNotesWidget,
  RevenueGoalWidget,
  TelegramLeadsWidget,
  TodaysStatsWidget,
  WeekStripWidget,
} from '../components/dashboard/index.js';
import { useDashboardLayoutStore } from '../features/dashboard/dashboardLayoutStore.js';
import { useManagerBriefing } from '../features/dashboard/hooks/useManagerBriefing.js';
import { useSalesHealthOverview } from '../features/dashboard/hooks/useSalesHealthOverview.js';
import {
  DASHBOARD_PRESETS,
  DASHBOARD_WIDGET_SPAN_OPTIONS,
  getAvailableDashboardWidgets,
  getPresetLabel,
  getWidgetDefinition,
  normalizeWidgetSpan,
  type DashboardPresetId,
  type DashboardWidgetDefinition,
  type DashboardWidgetId,
  type DashboardWidgetSpan,
} from '../features/dashboard/dashboardPresets.js';
import { useDashboardViewModel } from '../features/dashboard/useDashboardViewModel.js';
import { buildDashboardMyDayCommand } from '../features/ai-chat/lib/myDayScenarios.js';
import {
  updateDashboardOnboarding,
  type ManagerBriefingPriorityKind,
} from '../features/dashboard/api/dashboard.client.js';
import { AgentWidgetsSection } from '../features/dashboard/components/AgentWidgetsSection.js';
import { DashboardSavedViews } from '../features/views/components/DashboardSavedViews.js';
import { MorningBriefingBanner } from '../components/dashboard/MorningBriefingBanner.js';

const DASHBOARD_PRESET_ICONS: Readonly<Record<DashboardPresetId, LucideIcon>> = {
  ceo: Crown,
  sales: TrendingUp,
  pm: Briefcase,
  dev: Code2,
  creative: Palette,
  flatmedia: LayoutDashboard,
} as const;

const PRIMARY_PRESET_IDS: readonly DashboardPresetId[] = ['ceo', 'sales', 'pm'];

const MAX_ROW_SPAN = 12;

function spanToWidthKey(span: DashboardWidgetSpan): string {
  switch (span) {
    case 4:
      return 'one_third';
    case 6:
      return 'half';
    case 8:
      return 'two_thirds';
    case 12:
      return 'full';
    default:
      return 'full';
  }
}

interface WidgetSource {
  id: DashboardWidgetId;
  def: DashboardWidgetDefinition;
  /** Effective desktop span (override or registry default). */
  span: number;
}

/** Widget plus the column span it occupies inside its row (always full rows). */
type WidgetCell = WidgetSource;

interface WidgetRow {
  key: string;
  cells: WidgetCell[];
}

/**
 * Build widget rows using a deterministic greedy fill:
 *  - widgets with span >= 12 always occupy a full-width row alone;
 *  - adjacent weighted widgets whose spans fit share a row, keeping their
 *    original spans (e.g. 8 + 4 = 12 becomes 8/12 + 4/12);
 *  - a widget that cannot fit the current batch ends that batch, so a single
 *    widget never forces an unrelated following widget onto a separate row;
 *  - every weighted row is normalized so the cells sum to exactly 12 columns.
 *    If a batch's spans total less than 12, the deficit is added to the widest
 *    cell (deterministic: first widest on ties). This keeps the grid gap-free
 *    while preserving the intended relative size of each widget.
 *
 * DOM order is preserved; narrow screens collapse every widget to full width
 * via the `.dashboard-grid` media query.
 */
function normalizeRow(batch: readonly WidgetSource[]): WidgetCell[] {
  const total = batch.reduce((sum, item) => sum + item.span, 0);
  if (batch.length === 1) {
    return [{ ...batch[0]!, span: MAX_ROW_SPAN }];
  }

  if (total <= MAX_ROW_SPAN) {
    const cells = batch.map<WidgetCell>((item) => ({ ...item, span: item.span }));
    const deficit = MAX_ROW_SPAN - total;
    if (deficit > 0) {
      // Deterministic tie-break: the first widest cell absorbs the whole gap.
      const widest = cells.reduce((a, b) => (a.span >= b.span ? a : b));
      widest.span += deficit;
    }
    return cells;
  }

  // Fallback for any overloaded row (should not happen with current greedy
  // batching, but kept so the layout never exceeds 12 columns).
  const cells: WidgetCell[] = batch.slice(0, -1).map((item) => ({
    ...item,
    span: Math.max(1, Math.round((MAX_ROW_SPAN * item.span) / total)),
  }));
  const used = cells.reduce((sum, cell) => sum + cell.span, 0);
  const last = batch[batch.length - 1]!;
  const lastSpan = Math.max(1, MAX_ROW_SPAN - used);
  cells.push({ ...last, span: lastSpan });

  // Rounding can overshoot; shave the widest cell until the row sums to 12.
  let overshoot = cells.reduce((sum, cell) => sum + cell.span, 0) - MAX_ROW_SPAN;
  while (overshoot > 0) {
    const widest = cells.reduce((a, b) => (a.span >= b.span ? a : b));
    const shave = Math.min(widest.span - 1, overshoot);
    widest.span -= shave;
    overshoot -= shave;
  }
  return cells;
}

function buildWidgetRows(items: readonly WidgetSource[]): WidgetRow[] {
  const rows: WidgetRow[] = [];
  let batch: WidgetSource[] = [];
  let batchSpan = 0;

  const closeBatch = (): void => {
    if (batch.length === 0) return;
    rows.push({ key: batch.map((item) => item.id).join('+'), cells: normalizeRow(batch) });
    batch = [];
    batchSpan = 0;
  };

  for (const item of items) {
    const span = item.span;
    if (span >= MAX_ROW_SPAN) {
      closeBatch();
      rows.push({ key: item.id, cells: [{ ...item, span: MAX_ROW_SPAN }] });
      continue;
    }

    if (batchSpan + span <= MAX_ROW_SPAN) {
      batch = [...batch, item];
      batchSpan += span;
    } else {
      closeBatch();
      batch = [item];
      batchSpan = span;
    }
  }
  closeBatch();
  return rows;
}

export default function DashboardPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { viewModel, dealsLoading, hotLeadsLoading, hasError, refetchAll } =
    useDashboardViewModel();
  const {
    data: briefing,
    isLoading: briefingLoading,
    isError: briefingIsError,
    error: briefingQueryError,
    refetch: refetchBriefing,
  } = useManagerBriefing();
  const {
    data: salesHealth,
    isLoading: salesHealthLoading,
    error: salesHealthQueryError,
  } = useSalesHealthOverview();
  const activePresetId = useDashboardLayoutStore((state) => state.activePresetId);
  const layout = useDashboardLayoutStore((state) => state.layout);
  const applyPreset = useDashboardLayoutStore((state) => state.applyPreset);
  const resetLayout = useDashboardLayoutStore((state) => state.resetLayout);
  const addWidget = useDashboardLayoutStore((state) => state.addWidget);
  const removeWidget = useDashboardLayoutStore((state) => state.removeWidget);
  const moveWidget = useDashboardLayoutStore((state) => state.moveWidget);
  const setWidgetSpan = useDashboardLayoutStore((state) => state.setWidgetSpan);
  const [customizing, setCustomizing] = useState(false);

  // First-run checklist: the dismiss click persists server-side; the refetch
  // drops the widget from the viewModel.
  const queryClient = useQueryClient();
  const dismissOnboardingMutation = useMutation({
    mutationFn: () => updateDashboardOnboarding({ dismissed: true }),
    onSuccess: () => {
      // Best effort refresh: the ['dashboard'] prefix covers every
      // dashboard-scoped query family (deals, stats, onboarding, ...).
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }).catch(console.error);
    },
  });

  const handleApplyPreset = (presetId: DashboardPresetId): void => {
    applyPreset(presetId);
    setCustomizing(false);
  };

  const handleRunBriefingAi = (kind: ManagerBriefingPriorityKind): void => {
    if (briefing === undefined) {
      return;
    }
    openChatCommand(
      navigate,
      buildDashboardMyDayCommand({ ...briefing, meetings: briefing.todayMeetings }, kind),
    );
  };

  // Retention hook: hand a scoped deal ID to the same command consumer as My day.
  const handleProposeTouchStrategy = (dealId: string): void => {
    openChatCommand(navigate, commandRequest('touchStrategy', { dealId }));
  };

  // Pick the first briefing priority that actually has records behind it
  // (same record-count mapping as MyDayWidget); empty briefing disables the CTA.
  const briefingRecordCounts: Record<ManagerBriefingPriorityKind, number> | undefined =
    briefing === undefined
      ? undefined
      : {
          meetings: briefing.todayMeetings.length,
          overdue_tasks: briefing.overdueTasks.length,
          risk_deals: briefing.riskDeals.length,
          hot_leads: briefing.hotLeads.length,
        };
  const firstRunnablePriority =
    briefingRecordCounts === undefined
      ? undefined
      : (briefing?.priorities ?? []).find((priority) => briefingRecordCounts[priority.kind] > 0)
          ?.kind;
  const bannerRunAiDisabled = briefing !== undefined && firstRunnablePriority === undefined;
  const handleRunBannerAi = (): void => {
    if (firstRunnablePriority !== undefined) {
      handleRunBriefingAi(firstRunnablePriority);
    }
  };

  const renderWidgetActions = (widgetId: DashboardWidgetId): ReactElement | null => {
    if (!customizing) {
      return null;
    }

    const index = layout.findIndex((item) => item.id === widgetId);
    const layoutItem = layout[index];
    const widgetDef = getWidgetDefinition(widgetId);
    const widgetLabel = t(`crm.dashboard.widgets.${widgetId}`);
    const currentSpan =
      layoutItem?.spanOverride ?? (widgetDef ? normalizeWidgetSpan(widgetDef.span) : 12);

    const widthOptions = DASHBOARD_WIDGET_SPAN_OPTIONS.map((span) => ({
      value: span,
      label: t(`crm.dashboard.customize.width_label.${spanToWidthKey(span)}`),
      'aria-label': t(`crm.dashboard.customize.width_aria.${spanToWidthKey(span)}`),
    }));

    const handleSpanChange = (value: number): void => {
      const span = value as DashboardWidgetSpan;
      if (DASHBOARD_WIDGET_SPAN_OPTIONS.includes(span)) {
        setWidgetSpan(widgetId, span);
      }
    };

    return (
      <div
        className="flex justify-end"
        style={{
          gap: 4,
          padding: '2px 6px',
          background: 'var(--ant-color-bg-container)',
          border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
          borderBottom: 0,
          borderRadius: 'var(--ant-border-radius-lg, 14px) var(--ant-border-radius-lg, 14px) 0 0',
        }}
      >
        {widgetId !== 'savedViews' && (
          <>
            <span
              className="self-center text-[12px]"
              style={{ color: 'var(--ant-color-text-tertiary)' }}
            >
              {t('crm.dashboard.customize.width_for', { label: widgetLabel })}
            </span>
            <Select
              size="small"
              value={currentSpan}
              onChange={handleSpanChange}
              options={widthOptions}
              aria-label={t('crm.dashboard.customize.width_for', { label: widgetLabel })}
              style={{ minWidth: 100 }}
            />
            {layoutItem?.spanOverride !== undefined && (
              <Button
                size="small"
                type="text"
                aria-label={t('crm.dashboard.customize.clear_width', { label: widgetLabel })}
                onClick={() => setWidgetSpan(widgetId, null)}
              >
                {t('crm.dashboard.customize.clear_width')}
              </Button>
            )}
          </>
        )}
        {index > 0 && (
          <Button
            size="small"
            type="text"
            icon={<ChevronUp size={14} />}
            aria-label={t('crm.dashboard.customize.move_up', { label: widgetLabel })}
            onClick={() => moveWidget(widgetId, -1)}
          />
        )}
        {index < layout.length - 1 && (
          <Button
            size="small"
            type="text"
            icon={<ChevronDown size={14} />}
            aria-label={t('crm.dashboard.customize.move_down', { label: widgetLabel })}
            onClick={() => moveWidget(widgetId, 1)}
          />
        )}
        <Button
          size="small"
          type="text"
          danger
          icon={<X size={14} />}
          aria-label={t('crm.dashboard.customize.remove', { label: widgetLabel })}
          onClick={() => removeWidget(widgetId)}
        />
      </div>
    );
  };

  const renderWidget = (widgetId: DashboardWidgetId): ReactElement | null => {
    switch (widgetId) {
      case 'stats':
        return <TodaysStatsWidget stats={viewModel.stats} />;
      case 'myDay':
        return (
          <MyDayWidget
            {...(briefing !== undefined ? { briefing } : {})}
            loading={briefingLoading}
            error={briefingQueryError}
            onRetry={() => void refetchBriefing()}
            {...(salesHealth !== undefined ? { salesHealth } : {})}
            salesHealthLoading={salesHealthLoading}
            salesHealthError={salesHealthQueryError}
            onOpenDeal={(dealId) => navigate(`/deals/${dealId}`)}
            onOpenTask={(taskId) => navigate(`/tasks/${taskId}`)}
            onOpenMeeting={(meetingId) => navigate(`/meetings/${meetingId}`)}
            onRunPriority={handleRunBriefingAi}
            onProposeTouchStrategy={handleProposeTouchStrategy}
            testId="dashboard-widget-my-day"
          />
        );
      case 'focus':
        return (
          <FocusTodayWidget
            items={viewModel.focusItems}
            loading={dealsLoading || hotLeadsLoading}
            testId="dashboard-widget-focus"
            onViewAll={() => navigate('/hot-leads')}
            onItemClick={(item) => navigate(item.targetUrl ?? `/deals/${item.id}`)}
          />
        );
      case 'pipelineBoard':
        return (
          <PipelineBoardWidget
            cards={viewModel.pipelineCards}
            loading={dealsLoading}
            testId="dashboard-widget-pipeline-board"
            onOpenDeal={(dealId) => navigate(`/deals/${dealId}`)}
            onOpenKanban={() => navigate('/deals')}
          />
        );
      case 'week':
        return (
          <WeekStripWidget
            testId="dashboard-widget-week"
            onOpenCalendar={() => navigate('/calendar')}
          />
        );
      case 'revenueGoal':
        return (
          <RevenueGoalWidget
            testId="dashboard-widget-revenue-goal"
            onOpenDeals={() => navigate('/deals')}
          />
        );
      case 'telegramLeads':
        return (
          <TelegramLeadsWidget
            testId="dashboard-widget-telegram-leads"
            onOpenQueue={() => navigate('/telegram-candidates')}
          />
        );
      case 'savedViews':
        return <DashboardSavedViews testId="dashboard-widget-saved-views" />;
      case 'workforce':
        return (
          <AgentStatusWidget agents={viewModel.workforce} testId="dashboard-widget-workforce" />
        );
      case 'burndown':
        return (
          <BurndownWidget
            data={viewModel.burndownData}
            loading={dealsLoading}
            testId="dashboard-widget-burndown"
          />
        );
      case 'pipelines':
        return (
          <ActivePipelinesWidget
            pipelines={viewModel.pipelines}
            loading={dealsLoading}
            testId="dashboard-widget-pipelines"
            onItemClick={() => navigate('/deals')}
          />
        );
      case 'activity':
        return (
          <RecentActivityWidget
            items={viewModel.activityItems}
            loading={dealsLoading}
            testId="dashboard-widget-activity"
            onItemClick={(item) => navigate(item.dealId ? `/deals/${item.dealId}` : '/deals')}
          />
        );
      case 'calendar':
        return (
          <CalendarWidget
            deadlines={viewModel.deadlines}
            loading={dealsLoading}
            testId="dashboard-widget-calendar"
            onItemClick={(deadline) => navigate(deadline.id ? `/deals/${deadline.id}` : '/deals')}
          />
        );
      case 'actions':
        return (
          <QuickActionsWidget
            testId="dashboard-widget-actions"
            actions={[
              {
                id: 'new-project',
                label: t('crm.dashboard.actions.new_project.label'),
                subtitle: t('crm.dashboard.actions.new_project.subtitle'),
                color: 'var(--color-brand-500)',
                background: 'var(--ant-color-primary-bg)',
                icon: Briefcase,
                onClick: () => navigate('/deals'),
              },
              {
                id: 'summon-agent',
                label: t('crm.dashboard.actions.summon_agent.label'),
                subtitle: t('crm.dashboard.actions.summon_agent.subtitle'),
                color: 'var(--ant-color-info)',
                background: 'var(--ant-color-info-bg)',
                icon: Code2,
                onClick: () => navigate('/hot-leads'),
              },
              {
                id: 'new-pipeline',
                label: t('crm.dashboard.actions.new_pipeline.label'),
                subtitle: t('crm.dashboard.actions.new_pipeline.subtitle'),
                color: 'var(--ant-color-success)',
                background: 'var(--ant-color-success-bg)',
                icon: LayoutDashboard,
                onClick: () => navigate('/deals'),
              },
              {
                id: 'analytics',
                label: t('crm.dashboard.actions.analytics.label'),
                subtitle: t('crm.dashboard.actions.analytics.subtitle'),
                color: 'var(--ant-color-primary)',
                background: 'var(--ant-color-primary-bg)',
                icon: Palette,
                onClick: () => navigate('/dashboard'),
              },
              {
                id: 'settings',
                label: t('crm.dashboard.actions.settings.label'),
                subtitle: t('crm.dashboard.actions.settings.subtitle'),
                color: 'var(--ant-color-text-secondary)',
                background: 'var(--g-fill)',
                icon: Crown,
                onClick: () => navigate('/settings'),
              },
            ]}
          />
        );
      case 'notifications':
        return (
          <NotificationCenterWidget
            notifications={viewModel.notifications}
            loading={hotLeadsLoading}
            testId="dashboard-widget-notifications"
            onItemClick={(item) =>
              navigate(item.targetUrl ?? (item.dealId ? `/deals/${item.dealId}` : '/deals'))
            }
          />
        );
      case 'notes':
        return (
          <RecentNotesWidget testId="dashboard-widget-notes" onViewAll={() => navigate('/notes')} />
        );
      case 'openTasks':
        return (
          <OpenTasksWidget
            testId="dashboard-widget-open-tasks"
            onItemClick={(task) => navigate(`/tasks/${task.id}`)}
            onViewAll={() => navigate('/tasks')}
          />
        );
      case 'attentionQueue':
        return (
          <AttentionQueueWidget
            testId="dashboard-widget-attention-queue"
            onOpenDeal={(dealId) => navigate(`/deals/${dealId}`)}
            onViewAll={() => navigate('/deals')}
          />
        );
      default:
        return null;
    }
  };

  const widgetItems: WidgetSource[] = [];
  for (const item of layout) {
    const def = getWidgetDefinition(item.id);
    if (def && def.available) {
      const span = item.spanOverride ?? normalizeWidgetSpan(def.span);
      widgetItems.push({ id: item.id, def, span });
    }
  }

  const availableWidgets = getAvailableDashboardWidgets().filter(
    (widget) => !layout.some((item) => item.id === widget.id),
  );

  const primaryPresets = DASHBOARD_PRESETS.filter((preset) =>
    PRIMARY_PRESET_IDS.includes(preset.id),
  );

  const hasSavedViews = widgetItems.some(({ id }) => id === 'savedViews');

  const rows = buildWidgetRows(widgetItems);

  const renderWidgetCell = ({ id: widgetId, span }: WidgetCell): ReactElement | null => {
    const content = renderWidget(widgetId);
    if (!content) return null;

    const testId =
      widgetId === 'focus'
        ? 'dashboard-focus-list'
        : widgetId === 'workforce'
          ? 'dashboard-workforce-summary'
          : widgetId === 'pipelines'
            ? 'dashboard-pipelines'
            : undefined;

    return (
      <div
        key={widgetId}
        className="dashboard-widget"
        data-testid={testId}
        style={{ '--widget-span': span } as CSSProperties}
      >
        {renderWidgetActions(widgetId)}
        {content}
        {/* Agent widgets travel with the saved-views row they followed before:
            rendering inside this full-width cell keeps them in the same flow
            position without a separate (possibly empty) grid row. */}
        {widgetId === 'savedViews' ? <AgentWidgetsSection customizing={customizing} /> : null}
      </div>
    );
  };

  return (
    <div className="animate-fade-in flex flex-col w-full" data-testid="dashboard-mission-control">
      {/* Fluid width: fills the AppLayout content column (capped at 1600px
          app-wide). A tighter 1280px cap left a dead zone to the right on
          wide screens; the widget grid and KPI strip stretch with the column
          while keeping their span proportions. */}
      <div className="flex flex-col w-full max-w-[1600px] mx-auto">
        {/* Header Area — Warm Precision pagehead: 17px title, 12px sub. */}
        <header
          className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 shrink-0 z-10 w-full"
          style={{ margin: '4px 0 16px' }}
        >
          <div className="min-w-0">
            <h1
              data-testid="dashboard-title"
              className="m-0 leading-tight"
              style={{
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: 'var(--ant-color-text-heading, #292524)',
              }}
            >
              {t('crm.dashboard.page.title')}
            </h1>
            <p
              data-testid="dashboard-subtitle"
              className="m-0"
              style={{
                color: 'var(--ant-color-text-tertiary, #6d6963)',
                fontSize: 12,
                marginTop: 2,
              }}
            >
              {t('crm.dashboard.page.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="inline-flex overflow-hidden"
              role="group"
              aria-label={t('crm.dashboard.presets.group')}
              style={{
                border: '1px solid var(--ant-color-border, #d6d3d1)',
                borderRadius: 'var(--ant-border-radius, 10px)',
                fontSize: 12,
              }}
            >
              {primaryPresets.map((preset, index) => {
                const PresetIcon = DASHBOARD_PRESET_ICONS[preset.id];
                const isActive = activePresetId === preset.id;

                return (
                  <button
                    key={preset.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => handleApplyPreset(preset.id)}
                    className="flex items-center gap-1.5 border-0 px-2.5 py-1 text-[12px] font-medium transition-colors"
                    style={{
                      backgroundColor: isActive
                        ? 'var(--ant-color-primary-bg, #fffbeb)'
                        : 'transparent',
                      color: isActive
                        ? 'var(--ant-color-primary-text, #b45309)'
                        : 'var(--ant-color-text-tertiary, #6d6963)',
                      fontWeight: isActive ? 550 : 500,
                      cursor: 'pointer',
                      ...(index > 0
                        ? { borderLeft: '1px solid var(--ant-color-border-secondary, #e7e5e4)' }
                        : {}),
                    }}
                  >
                    <PresetIcon size={13} />
                    {getPresetLabel(preset.id, t)}
                  </button>
                );
              })}
            </div>
            {customizing ? (
              <Button onClick={resetLayout} style={{ height: 28, fontSize: 12, paddingInline: 10 }}>
                {t('crm.dashboard.customize.reset')}
              </Button>
            ) : null}
            <Button
              icon={<Settings size={14} />}
              onClick={() => setCustomizing((value) => !value)}
              data-testid="dashboard-customize-toggle"
              style={{ height: 28, fontSize: 12, paddingInline: 10 }}
            >
              {customizing
                ? t('crm.dashboard.customize.done')
                : t('crm.dashboard.customize.button')}
            </Button>
          </div>
        </header>

        {/* Widgets Grid — Warm Precision: 12-column hairline grid, 12px gutters. */}
        <main className="flex flex-col w-full" style={{ gap: 12 }}>
          {/* First-run onboarding checklist: rendered until the user dismisses
              it (server state), independent of the customizable widget grid. */}
          {!viewModel.onboarding.dismissed && viewModel.onboarding.id ? (
            <OnboardingChecklistWidget
              state={viewModel.onboarding}
              onDismissHook={() => dismissOnboardingMutation.mutate()}
              testId="dashboard-widget-onboarding"
            />
          ) : null}
          <MorningBriefingBanner
            {...(briefing !== undefined ? { briefing } : {})}
            loading={briefingLoading}
            error={briefingIsError}
            onRetry={() => void refetchBriefing()}
            onRunAi={handleRunBannerAi}
            runAiDisabled={bannerRunAiDisabled}
          />
          {hasError ? (
            // Shared error surface with the standard retry (was a bare Alert
            // with its own button). `crm.shared.retry` === the old label.
            <ErrorState
              title={t('crm.dashboard.error.load_failed')}
              onRetry={() => void refetchAll()}
            />
          ) : null}
          <div className="dashboard-grid">
            {rows.map((row) => (
              // Key by content, not position: reordering widgets regroups rows,
              // and an index key would remount widget state into the wrong row.
              <div key={`dashboard-row-${row.key}`} className="dashboard-row">
                {row.cells.map((cell) => renderWidgetCell(cell))}
              </div>
            ))}
          </div>
          {hasSavedViews ? null : <AgentWidgetsSection customizing={customizing} />}
        </main>
      </div>

      {/* Widget Picker */}
      {customizing && availableWidgets.length > 0 && (
        <Card
          size="small"
          title={
            <span
              className="uppercase font-semibold"
              style={{
                color: 'var(--ant-color-text-secondary)',
                fontSize: 11,
                letterSpacing: '0.07em',
              }}
            >
              {t('crm.dashboard.customize.add_widget')}
            </span>
          }
          variant="borderless"
          style={{ borderRadius: 0, borderTop: '1px solid var(--ant-color-border-secondary)' }}
        >
          <Space wrap>
            {availableWidgets.map((widget) => (
              <Button
                key={widget.id}
                size="small"
                aria-label={t('crm.dashboard.customize.add', {
                  label: t(`crm.dashboard.widgets.${widget.id}`),
                })}
                icon={<Plus size={14} />}
                onClick={() => addWidget(widget.id)}
              >
                {t(`crm.dashboard.widgets.${widget.id}`)}
              </Button>
            ))}
          </Space>
        </Card>
      )}
      {customizing && (
        <div
          className="w-full text-center"
          style={{
            padding: '12px 16px',
            color: 'var(--ant-color-text-tertiary)',
            fontSize: 12,
          }}
        >
          {t('crm.dashboard.customize.local_hint')}
        </div>
      )}
    </div>
  );
}
